/**
 * ChangeTracker.ts - Track and manage style changes with undo/redo
 * 
 * CHANGE HISTORY:
 * - 251102_1246 danielbeer: Initial creation for universal design editor
 * 
 * Provides:
 * - Change history tracking
 * - Undo/redo functionality
 * - Change batching
 * - Change metadata
 */

export interface StyleChange {
  id: string;
  timestamp: number;
  type: 'create' | 'update' | 'delete' | 'apply';
  target: {
    type: 'style' | 'image' | 'theme' | 'object';
    id: string;
    name?: string;
  };
  changes: {
    property: string;
    oldValue: any;
    newValue: any;
  }[];
  metadata?: {
    userId?: string;
    description?: string;
    batch?: string; // Group related changes
  };
}

export interface ChangeTrackerOptions {
  maxHistory?: number;
  enableBatching?: boolean;
  actr?: any;
}

export class ChangeTracker {
  private history: StyleChange[];
  private currentIndex: number;
  private maxHistory: number;
  private enableBatching: boolean;
  private currentBatch: string | null;
  private batchChanges: StyleChange[];
  private actr: any;
  private listeners: Set<Function>;

  constructor(options: ChangeTrackerOptions = {}) {
    this.history = [];
    this.currentIndex = -1;
    this.maxHistory = options.maxHistory || 100;
    this.enableBatching = options.enableBatching !== false;
    this.currentBatch = null;
    this.batchChanges = [];
    this.actr = options.actr;
    this.listeners = new Set();
  }

  /**
   * Record a change
   */
  recordChange(change: Omit<StyleChange, 'id' | 'timestamp'>): void {
    const fullChange: StyleChange = {
      ...change,
      id: this.generateId(),
      timestamp: Date.now()
    };

    // If batching, add to batch
    if (this.currentBatch && this.enableBatching) {
      fullChange.metadata = {
        ...fullChange.metadata,
        batch: this.currentBatch
      };
      this.batchChanges.push(fullChange);
      return;
    }

    // Add to history
    this.addToHistory(fullChange);
  }

  /**
   * Start a batch of changes
   */
  startBatch(batchId?: string): string {
    this.currentBatch = batchId || this.generateId();
    this.batchChanges = [];
    return this.currentBatch;
  }

  /**
   * End current batch
   */
  endBatch(): void {
    if (!this.currentBatch) return;

    // Add all batch changes to history
    this.batchChanges.forEach(change => {
      this.addToHistory(change);
    });

    this.currentBatch = null;
    this.batchChanges = [];
    
    this.notifyListeners('batchEnded', { changeCount: this.batchChanges.length });
  }

  /**
   * Cancel current batch
   */
  cancelBatch(): void {
    this.currentBatch = null;
    this.batchChanges = [];
    this.notifyListeners('batchCancelled', {});
  }

  /**
   * Undo last change
   */
  undo(): StyleChange | null {
    if (!this.canUndo()) return null;

    const change = this.history[this.currentIndex];
    this.currentIndex--;

    this.notifyListeners('undo', { change });
    
    if (this.actr) {
      this.actr.execute('stylr:undo', { changeId: change.id });
    }

    return change;
  }

  /**
   * Redo last undone change
   */
  redo(): StyleChange | null {
    if (!this.canRedo()) return null;

    this.currentIndex++;
    const change = this.history[this.currentIndex];

    this.notifyListeners('redo', { change });
    
    if (this.actr) {
      this.actr.execute('stylr:redo', { changeId: change.id });
    }

    return change;
  }

  /**
   * Check if can undo
   */
  canUndo(): boolean {
    return this.currentIndex >= 0;
  }

  /**
   * Check if can redo
   */
  canRedo(): boolean {
    return this.currentIndex < this.history.length - 1;
  }

  /**
   * Get change history
   */
  getHistory(limit?: number): StyleChange[] {
    const history = this.history.slice(0, this.currentIndex + 1);
    return limit ? history.slice(-limit) : history;
  }

  /**
   * Get changes by target
   */
  getChangesByTarget(targetType: string, targetId: string): StyleChange[] {
    return this.history.filter(change => 
      change.target.type === targetType && change.target.id === targetId
    );
  }

  /**
   * Get changes by batch
   */
  getChangesByBatch(batchId: string): StyleChange[] {
    return this.history.filter(change => 
      change.metadata?.batch === batchId
    );
  }

  /**
   * Clear history
   */
  clearHistory(): void {
    this.history = [];
    this.currentIndex = -1;
    this.notifyListeners('historyCleared', {});
  }

  /**
   * Get change statistics
   */
  getStatistics(): {
    totalChanges: number;
    changesByType: Record<string, number>;
    changesByTarget: Record<string, number>;
    recentChanges: number;
  } {
    const now = Date.now();
    const oneHourAgo = now - (60 * 60 * 1000);

    const changesByType: Record<string, number> = {};
    const changesByTarget: Record<string, number> = {};
    let recentChanges = 0;

    this.history.forEach(change => {
      // Count by type
      changesByType[change.type] = (changesByType[change.type] || 0) + 1;
      
      // Count by target type
      changesByTarget[change.target.type] = (changesByTarget[change.target.type] || 0) + 1;
      
      // Count recent
      if (change.timestamp > oneHourAgo) {
        recentChanges++;
      }
    });

    return {
      totalChanges: this.history.length,
      changesByType,
      changesByTarget,
      recentChanges
    };
  }

  /**
   * Export history
   */
  exportHistory(): string {
    return JSON.stringify({
      history: this.history,
      currentIndex: this.currentIndex,
      exportedAt: Date.now()
    }, null, 2);
  }

  /**
   * Import history
   */
  importHistory(data: string): void {
    try {
      const parsed = JSON.parse(data);
      this.history = parsed.history || [];
      this.currentIndex = parsed.currentIndex ?? -1;
      this.notifyListeners('historyImported', { changeCount: this.history.length });
    } catch (error) {
      throw new Error(`Failed to import history: ${error}`);
    }
  }

  /**
   * Add event listener
   */
  on(callback: Function): void {
    this.listeners.add(callback);
  }

  /**
   * Remove event listener
   */
  off(callback: Function): void {
    this.listeners.delete(callback);
  }

  /**
   * Add change to history
   */
  private addToHistory(change: StyleChange): void {
    // Remove any changes after current index (redo history)
    if (this.currentIndex < this.history.length - 1) {
      this.history = this.history.slice(0, this.currentIndex + 1);
    }

    // Add new change
    this.history.push(change);
    this.currentIndex++;

    // Trim history if needed
    if (this.history.length > this.maxHistory) {
      const removeCount = this.history.length - this.maxHistory;
      this.history = this.history.slice(removeCount);
      this.currentIndex -= removeCount;
    }

    this.notifyListeners('changeRecorded', { change });
  }

  /**
   * Notify listeners
   */
  private notifyListeners(event: string, data: any): void {
    this.listeners.forEach(listener => {
      try {
        listener(event, data);
      } catch (error) {
        console.error('Error in ChangeTracker listener:', error);
      }
    });
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `change_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }
}

/**
 * Singleton instance
 */
let changeTrackerInstance: ChangeTracker | null = null;

export function getChangeTracker(options?: ChangeTrackerOptions): ChangeTracker {
  if (!changeTrackerInstance) {
    changeTrackerInstance = new ChangeTracker(options);
  }
  return changeTrackerInstance;
}

export function resetChangeTracker(): void {
  changeTrackerInstance = null;
}
